import {
  Box,
  Grid2 as Grid,
  IconButton,
  styled,
  Typography
} from '@mui/material'
import ButtonBase from 'design-system/button'
import NavLinkBase from 'design-system/NavLink'
import { ADD_PROFESSIONALS } from 'navigation/CONSTANTS'
import React, { useState } from 'react'
import useProfessional from './useProfessionals'
import { GridColDef } from '@mui/x-data-grid'
import { IUserSession } from 'types'
import AddIcon from '@mui/icons-material/Add'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { IProssionalRoleTypeEnum } from 'types/user/enum'
import DataGridBase from 'design-system/DataGrid'
import ConfirmationModal from 'components/ConfirmationModal'
import PermissionByRole from 'components/rules/PermissionByRole'
import { useToast } from 'services/context/toast'

const StyledDataGrid = styled(DataGridBase<IUserSession>)(() => ({
  '& .MuiDataGrid-topContainer': {
    '& [role="row"]': {
      backgroundColor: 'var(--primarySoft-button-color)'
    },
    '&, & svg': {
      color: '#fff'
    }
  }
}))

const columnsGuide: GridColDef<IUserSession>[] = [
  {
    field: 'fullName',
    headerName: 'Nome',
    description: 'This column has a value getter and is not sortable.',
    sortable: true,
    valueGetter: (value, row) =>
      `${row.first_name.trim() || ''} ${row.last_name.trim() || ''}`,
    flex: 1
  },
  {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    flex: 1
  },
  {
    field: 'professional_license',
    headerName: 'Conselho',
    sortable: true,
    flex: 1
  },
  {
    field: 'professional_license_number',
    headerName: 'Documento',
    sortable: true,
    flex: 1
  },
  {
    field: 'specialization_name',
    headerName: 'Especialização',
    sortable: true,
    flex: 1
  },
  {
    field: 'role_type',
    headerName: 'Permissões',
    width: 100,
    sortable: false,
    renderCell: params =>
      params.value == IProssionalRoleTypeEnum.administrator
        ? 'Admin'
        : 'Regular'
  }
]

const Professional: React.FC = () => {
  const {
    professionals,
    isFilteredByEstablishment,
    state,
    handleEditRole,
    handleDeleteProfessional,
    deletionState
  } = useProfessional()

  const [professionalToRemove, setProfessionalToRemove] =
    useState<IUserSession | null>(null)

  const toast = useToast()

  const handleConfirmDeletion = async () => {
    if (!professionalToRemove || deletionState === 'loading') return

    const removed = await handleDeleteProfessional(
      professionalToRemove.professional_id
    )

    if (removed) {
      setProfessionalToRemove(null)
      toast.handler({
        content: 'Colaborador removido com sucesso!',
        severity: 'success'
      })
    } else {
      toast.handler({
        content: 'Houve um erro ao tentar remover o colaborador',
        severity: 'error'
      })
    }
  }

  const columns: GridColDef<IUserSession>[] = [
    ...columnsGuide,
    {
      field: 'settings',
      headerName: 'Ações',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <PermissionByRole
          rolesAllowed={[IProssionalRoleTypeEnum.administrator]}
        >
          <IconButton onClick={() => handleEditRole(params.row)}>
            <EditIcon />
          </IconButton>

          <IconButton
            color='error'
            aria-label='Remover colaborador'
            onClick={() => setProfessionalToRemove(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </PermissionByRole>
      )
    }
  ]

  return (
    <Box display={'flex'} sx={{ justifyContent: 'center' }}>
      <Grid container spacing={2} flex={1} maxWidth={'1200px'} p={2}>
        <Grid
          container
          size={12}
          spacing={1}
          justifyContent='space-between'
          alignItems={'center'}
        >
          <Typography variant='h5'>Colaboradores</Typography>
          <PermissionByRole
            rolesAllowed={[IProssionalRoleTypeEnum.administrator]}
          >
            <NavLinkBase to={ADD_PROFESSIONALS} absolute>
              <ButtonBase
                sx={{
                  '& .MuiButton-icon': {
                    marginLeft: '0px'
                  }
                }}
                endIcon={<AddIcon />}
              >
                <Typography variant='body1'>Adicionar colaborador</Typography>
              </ButtonBase>
            </NavLinkBase>
          </PermissionByRole>
        </Grid>
        <Grid flex={1}>
          <StyledDataGrid
            rows={professionals}
            columns={columns}
            getRowId={row => row.professional_id}
            loading={state == 'loading'}
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[25]}
            slotProps={{
              pagination: {
                labelDisplayedRows: ({ from, to, count }) =>
                  `Exibindo ${from}–${to} de ${count} registros`
              },
              noRowsOverlay: {
                'aria-label': isFilteredByEstablishment
                  ? 'Nenhum profissional encontrado'
                  : 'Selecione uma organização'
              }
            }}
          />
        </Grid>
      </Grid>
      <PermissionByRole rolesAllowed={[IProssionalRoleTypeEnum.administrator]}>
        <ConfirmationModal
          open={!!professionalToRemove}
          title='Deseja remover o colaborador da organização?'
          onConfirm={handleConfirmDeletion}
          onCancel={() => {
            if (deletionState !== 'loading') {
              setProfessionalToRemove(null)
            }
          }}
        />
      </PermissionByRole>
    </Box>
  )
}

export default Professional
